import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';

const CORE_FEATURES = new Set([
  'core_auth',
  'core_dashboards',
  'core_profiles',
  'core_feedback'
]);

const getInitialFeatures = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const userKey = localStorage.getItem('user_id') || localStorage.getItem('role') || 'auth';
    const storedUserKey = localStorage.getItem('feature_flags_user');
    if (storedUserKey && storedUserKey !== userKey) return null;
    const raw = localStorage.getItem('cached_feature_flags');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getInitialSubs = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return {};
    const userKey = localStorage.getItem('user_id') || localStorage.getItem('role') || 'auth';
    const storedUserKey = localStorage.getItem('feature_flags_user');
    if (storedUserKey && storedUserKey !== userKey) return {};
    const raw = localStorage.getItem('cached_feature_subs');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// ---------------------------------------------------------------------------
// Feature flag context (populated by GET /features/me at login + on tab-visibility
// + on auth_changed). Synchronously cached in localStorage to prevent layout shifts.
// Default context strictly gates to CORE_FEATURES only to prevent any flash of disabled content.
// ---------------------------------------------------------------------------
const FeatureFlagContext = createContext({
  features: null,
  subs: {},
  isEnabled: (key) => CORE_FEATURES.has(key),
  isSubEnabled: () => false,
  loading: false,
});

export function useFeatures() {
  return useContext(FeatureFlagContext);
}

export function FeatureFlagProvider({ children }) {
  const [features, setFeatures] = useState(getInitialFeatures);
  const [subs, setSubs] = useState(getInitialSubs);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setFeatures(null);
      setSubs({});
      try {
        localStorage.removeItem('cached_feature_flags');
        localStorage.removeItem('cached_feature_subs');
        localStorage.removeItem('feature_flags_user');
      } catch {}
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/features/me');
      if (mountedRef.current) {
        const feats = res.data?.features || {};
        const subData = res.data?.subs || {};
        const userKey = localStorage.getItem('user_id') || localStorage.getItem('role') || 'auth';
        setFeatures(feats);
        setSubs(subData);
        try {
          localStorage.setItem('cached_feature_flags', JSON.stringify(feats));
          localStorage.setItem('cached_feature_subs', JSON.stringify(subData));
          localStorage.setItem('feature_flags_user', userKey);
        } catch {}
      }
    } catch (err) {
      // On transient error, keep current features; if none, leave empty so core still works
      if (err?.response?.status !== 401 && mountedRef.current) {
        setFeatures((f) => f || {});
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    const onAuthChanged = () => refresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('auth_changed', onAuthChanged);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('auth_changed', onAuthChanged);
    };
  }, [refresh]);

  const isEnabled = useCallback((key) => {
    if (CORE_FEATURES.has(key)) return true;
    if (features) {
      return Boolean(features[key]);
    }
    // While loading before initial fetch: core features stay on, optional/gated features don't flash
    return false;
  }, [features]);

  const isSubEnabled = useCallback((feature, sub) => {
    if (CORE_FEATURES.has(feature)) return true;
    if (!features || !features[feature]) return false; // feature off => sub off
    const subsForFeature = subs && subs[feature] ? subs[feature] : {};
    return sub in subsForFeature ? Boolean(subsForFeature[sub]) : true;
  }, [features, subs]);

  return (
    <FeatureFlagContext.Provider value={{ features, subs, isEnabled, isSubEnabled, loading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

